import React, {useEffect} from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {connect} from 'react-redux';

import config from '../config/config';
import Sentry from '../config/sentry';
import useAutoSignout from '../hooks/useAutoSignout';

// auth
import NewAccountPage from './auth/NewAccountPage';
import LoginPage from './auth/LoginPage';
import MFAPage from './auth/MFAPage';
import HomeRedirectPage from './auth/HomeRedirectPage';
import ResetPasswordPage from './auth/ResetPasswordPage';
import CompletePasswordResetPage from './auth/CompletePasswordResetPage';
// patient onboarding
import PatientOnboardingPage from './patientSignup/PatientSignupWrapper';
import PublicBookingPage from './patientSignup/PublicBookingPage';
import OnboardingCompletedPage from './patientSignup/OnboardingCompletedPage';
// providers
import ProviderHomePage from './providers/ProviderHomePage';
import ViewProviderPage from './providers/ViewProviderPage';
import ListProvidersPage from './providers/ListProvidersPage';
import ProviderCalendarPage from './providers/ProviderCalendarPage';
// patients
import PatientHomePage from './patients/PatientHomePage';
import ListPatientsPage from './patients/ListPatientsPage';
import ViewPatientPage from './patients/ViewPatientPage';
// OLD scheduling
import AppointmentSuccessPage from './appointments/AppointmentSuccessPage';
import BrowseProvidersPage from './appointments/BrowseProvidersPage';
import PublicAppointmentPage from './appointments/PublicAppointmentPage';
import PatientAppointmentPage from './appointments/PatientAppointmentPage';
// NEW scheduling
import ScheduleWithPatientPage from './appointments/ScheduleWithPatientPage';
import ExistingPatientBookingPage from './booking/ExistingPatientBookingPage';
// cases
import ListCasesPage from './cases/ListCasesPage';
import NewCasePage from './cases/NewCasePage';
import ViewCasePage from './cases/ViewCasePage';
// leads
import ListLeadsPage from './leads/ListLeadsPage';
import LeadEventSignupPage from './leads/LeadEventSignupPage';
// messages
import ListMessagesPage from './messages/ListMessagesPage';
import ViewMessageThreadPage from './messages/ViewMessageThreadPage';
// payer plans
import ListPayerPlansPage from './payer-plans/ListPayerPlansPage';
// sessions
import ListSessionsPage from './sessions/ListSessionsPage';
import ViewSessionPage from './sessions/ViewSessionPage';
// session types & availability
import AvailabilityZonePage from './providers/AvailabilityZonePage';
import ListServiceTypesV2Page from './providers/ListSessionTypesV2Page';
import NewProviderSessionTypePage from './providers/NewProviderSessionTypePage';
import EditProviderSessionTypePage from './providers/EditProviderSessionTypePage';
import ListSessionTypesPage from './providers/ListSessionTypesPage';
// session type templates
import ListSessionTypeTemplatesPage from './providers/ListSessionTypeTemplatesPage';
// settings
import PatientPaymentPage from './patients/PatientPaymentPage';
import PatientSettingsPage from './patients/PatientSettingsPage';
import ProviderSettingsPage from './providers/ProviderSettingsPage';
import AccountSettingsPage from './providers/AccountSettingsPage';
import ProfileSettingsPage from './providers/ProfileSettingsPage';
// utilities
import ExceptionPage from './utilities/ExceptionPage';
import NotFoundPage from './utilities/NotFoundPage';
import MapboxDemo from './demos/MapboxDemo';
// embeds
import EmbedProviderSearch from './embed/EmbedProviderSearch';
// admin
import AdminHomePage from './admin/AdminHomePage';
import AdminProcessClaimsPage from './admin/AdminProcessClaimsPage';
import AdminMigrationPage from './admin/AdminMigrationPage';
import AssumePatientRolePage from './admin/AssumePatientRolePage';
import AssumeProviderRolePage from './admin/AssumeProviderRolePage';
import AdminLLMCallPage from './admin/AdminLLMCallPage';
import AdminSchedulingPage from './admin/AdminSchedulingPage';
import AdminGeolocationPage from './admin/AdminGeolocationPage';
import NewFacilityPage from './admin/NewFacilityPage';
import ListClaimsPage from './claims/ListClaimsPage';
import DataExportPage from './admin/DataExportPage';
import ViewReportPage from './reports/ViewReportPage';
import ListReportsPage from './reports/ListReportsPage';

// Wrap Routes with Sentry
const SentryRoutes = (
  config().useSentry
  ? Sentry.withSentryReactRouterV6Routing(Routes)
  : Routes
);

const HashRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (window.location.hash.startsWith("#/")) {
      const newPath = window.location.hash.replace("#", ""); // Convert #/my/path to /my/path
      window.history.replaceState(null, "", newPath); // Update URL without hash
      navigate(newPath);
    }
  }, []);
  return null;
};

const ReactRouter = BrowserRouter;

const Router = props => (
  <ReactRouter>
    <HashRedirect/>
    <SentryRoutes>
      <Route
        path="/"
        element={<HomeRedirectPage/>}
      />
      {/* Auth Pages */}
      <Route
        path='/login'
        element={<LoginPage/>}
      />
      <Route
        exact
        path='/signup'
        element={<NewAccountPage/>}
      />
      <Route
        exact
        path='/find-a-provider'
        element={<PublicBookingPage/>}
      />
      <Route
        exact
        path='/mfa'
        element={<MFAPage/>}
      />
      <Route
        exact
        path='/reset-password/start'
        element={<ResetPasswordPage/>}
      />
      <Route
        exact
        path='/reset-password/complete'
        element={<CompletePasswordResetPage/>}
      />
      {/* Provider-facing pages */}
      <Route
        exact
        path='/providers/cases/new'
        element={<NewCasePage/>}
      />
      <Route
        exact
        path='/providers/cases/view/:id'
        element={<ViewCasePage/>}
      />
      <Route
        exact
        path='/providers/cases'
        element={<ListCasesPage/>}
      />
      <Route
        exact
        path='/admin/leads/list'
        element={<ListLeadsPage isAdmin={true}/>}
      />
      <Route
        exact
        path='/admin/geolocation'
        element={<AdminGeolocationPage isAdmin={true}/>}
      />
      <Route
        exact
        path="/providers/patients"
        element={<ListPatientsPage/>}
      />
      <Route
        exact
        path="/providers/patients/view/:id"
        element={<ViewPatientPage/>}
      />
      <Route
        exact
        path="/providers/messages"
        element={<ListMessagesPage/>}
      />
      <Route
        exact
        path='/providers/browse'
        element={<ListProvidersPage/>}
      />
      <Route
        exact
        path='/providers/calendar'
        element={<ProviderCalendarPage/>}
      />
      <Route
        exact
        path='/providers/patients/messages/:id'
        element={<ViewMessageThreadPage/>}
      />
      <Route
        exact
        path='/providers/patients/:patientId/plans'
        element={<ListPayerPlansPage />}
      />
      <Route
        exact
        path="/providers/sessions"
        element={<ListSessionsPage/>}
      />
      <Route
        exact
        path='/providers/sessions/view/:id'
        element={<ViewSessionPage/>}
      />
      <Route
        exact
        path='/providers/signup'
        element={<NewAccountPage isProvider={true} />}
      />
      <Route
        exact
        path='/providers/settings/account'
        element={<AccountSettingsPage />}
      />
      <Route
        exact
        path='/providers/settings/profile'
        element={<ProfileSettingsPage />}
      />
      <Route
        exact
        path='/providers/settings/session-types/new'
        element={<NewProviderSessionTypePage />}
      />
      <Route
        exact
        path="/providers/service-types/list"
        element={<ListServiceTypesV2Page/>}
      />
      <Route 
        exact
        path='/providers/availabilities/list'
        element={<AvailabilityZonePage />}
      />
      <Route
        exact
        path='/providers/settings'
        element={<ProviderSettingsPage />}
      />
      <Route
        exact
        path='/providers/settings/session-types/edit/:id'
        element={<EditProviderSessionTypePage />}
      />
      <Route
        exact
        path='/session-types/:id'
        element={<EditProviderSessionTypePage/>}
      />
      <Route
        exact
        path='/providers/:id/session-types/list'
        element={<ListSessionTypesPage />}
      />
      <Route
        exact
        path='providers/settings/event-templates/:templateId/session-types/new'
        element={<NewProviderSessionTypePage />}
      />
      <Route
        exact
        path='/providers/settings/event-templates/list'
        element={<ListSessionTypeTemplatesPage />}
      />
      <Route
        exact
        path='/providers/view/:id'
        element={<ViewProviderPage />}
      />
      <Route
        exact
        path='/providers/appointments/patients/:id'
        element={<ScheduleWithPatientPage/>}
      />
      <Route
        exact
        path='/providers/:id/appointments/new'
        element={<PublicAppointmentPage />}
      />
      <Route
        exact
        path='/providers/leads/list'
        element={<ListLeadsPage isAdmin={false} />}
      />
      <Route
        exact
        path='/providers'
        element={<ProviderHomePage/>}
      />
      <Route
        exact
        path='/physical-therapists/:slug/book-now'
        element={<PublicAppointmentPage />}
      />
      <Route
        exact
        path='/personal-trainers/:slug/book-now'
        element={<PublicAppointmentPage />}
      />
      <Route
        path='/physical-therapists/:slug'
        element={<ViewProviderPage />}
      />
      <Route
        path='/personal-trainers/:slug'
        element={<ViewProviderPage />}
      />
      {/* Patient-facing pages */}
      <Route
        exact
        path='/clients'
        element={<PatientHomePage/>}
      />
      <Route
        exact
        path="/clients/view/:id"
        element={<ViewPatientPage/>}
      />
      <Route
        exact
        path='/clients/messages'
        element={<ListMessagesPage/>}
      />
      <Route
        exact
        path='/clients/messages/:id'
        element={<ViewMessageThreadPage/>}
      />
      <Route
        exact
        path='/clients/appointments/new/:providerId'
        element={<PatientAppointmentPage/>}
      />
      <Route
        exact
        path='/clients/booking'
        element={<ExistingPatientBookingPage/>}
      />
      <Route
        exact
        path='/appointments/success/:id'
        element={<AppointmentSuccessPage/>}
      />
      <Route
        exact
        path='/clients/browse-providers'
        element={<BrowseProvidersPage/>}
      />
      <Route
        exact
        path="/clients/sessions"
        element={<ListSessionsPage/>}
      />
      <Route
        exact
        path='/clients/sessions/view/:id'
        element={<ViewSessionPage/>}
      />
      <Route
        exact
        path='/clients/onboarding/completed'
        element={<OnboardingCompletedPage/>}
      />
      <Route
        exact
        path='/clients/onboarding'
        element={<PatientOnboardingPage/>}
      />
      <Route
        exact
        path='/clients/settings'
        element={<PatientSettingsPage/>}
      />
      <Route
        exact
        path='/clients/settings/payments'
        element={<PatientPaymentPage/>}
      />
      <Route
        exact
        path='/clients/settings/insurance'
        element={<ListPayerPlansPage/>}
      />
      <Route
        exact
        path="/clients/view/:id"
        element={<ViewPatientPage/>}
        />
      <Route
        exact
        path='/demo/mapbox'
        element={<MapboxDemo/>}
      />
      <Route
        exact
        path='/providers/:providerId/events/:eventType/signup'
        element={<LeadEventSignupPage />}
      />
      {/*  Admin Routes */}
      <Route
        exact
        path="/admin"
        element={<AdminHomePage/>}
      />
      <Route
        exact
        path="/admin/assume-patient-role"
        element={<AssumePatientRolePage/>}
      />
      <Route
        exact
        path="/admin/assume-provider-role"
        element={<AssumeProviderRolePage/>}
      />
      <Route
        exact
        path="/admin/migration"
        element={<AdminMigrationPage/>}
      />
      <Route
        exact
        path="/admin/process-claims"
        element={<AdminProcessClaimsPage />}
      />
      <Route
        exact
        path="/admin/llm-call"
        element={<AdminLLMCallPage/>}
      />
      <Route
        exact
        path='/admin/reports/list'
        element={<ListReportsPage/>}
      />
      <Route
        exact
        path='admin/reports/view/:id'
        element={<ViewReportPage/>}
      />
      <Route
        exact
        path="/admin/data-export"
        element={<DataExportPage/>}
      />
      <Route
        exact
        path="/admin/scheduling"
        element={<AdminSchedulingPage />}
      />
      <Route
        exact
        path="/admin/facilities/new"
        element={<NewFacilityPage />}
      />
      <Route
        exact
        path="/claims/list"
        element={<ListClaimsPage/>}
      />
      {/* Embed/Iframe Components */}
      <Route
        exact
        path='/embed/provider-search'
        element={<EmbedProviderSearch/>}
      />
      {/* Errors */}
      <Route
        path="/exception"
        element={<ExceptionPage/>}
      />
      <Route
        path="*"
        exact={true}
        element={<NotFoundPage/>}
      />
    </SentryRoutes>
  </ReactRouter>
);

const AppContainer = props => {
  useAutoSignout();
  return (
    <main>
      {props.children}
    </main>
  );
};

const App = props => (
  <AppContainer>
    <Router/>
  </AppContainer>
);

const mapState = state => ({});
const mapDispatch = dispatch => ({});

export default connect(mapState, mapDispatch)(App);
